import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { ProjectChevronLeftIcon } from '../ProjectPageIcons';
import PageBackButton, {
	isPlainLeftClick,
	useWebsiteBack
} from '../../../navigation/WebsiteNavigation';
import { LoadingSpinner } from '@corrprocanada/theme-kit/web';

import KpiPanel from './KpiPanel';

import './DetailPageKit.scss';

/**
 * DetailPageKit — shared building blocks for CorrGuard detail pages
 * (CorrMonitor asset details, Tracking run details, CorrLoader job
 * overview).  Presentational only; colours flow from the product theme
 * class (`cg-theme--*`) applied by the host page, so the same markup is
 * teal on Tracking, orange on CorrLoader / CP pages, etc.
 *
 * Block: `cg-detail`
 */

// ── Back link ──────────────────────────────────────────────────────
export const DetailBackLink = ({ to, label }) => {
	const goBack = useWebsiteBack(to);
	return (
		<Link
			to={to}
			className="cg-detail__back-link"
			onClick={event => {
				if (!event.defaultPrevented && isPlainLeftClick(event)) {
					event.preventDefault();
					goBack();
				}
			}}
		>
			<ProjectChevronLeftIcon />
			<span>{label}</span>
		</Link>
	);
};

// ── Page header ────────────────────────────────────────────────────
/**
 * @param icon      optional leading icon node (rendered in a tinted tile)
 * @param title     page title (string or node)
 * @param meta      node rendered under the title (badges, customer, job #…)
 * @param actions   node rendered on the right (buttons)
 */
export const DetailPageHeader = ({
	icon,
	title,
	meta,
	actions
}) => (
	<div className="cg-detail__page-header">
		<div className="cg-detail__page-header-left">
			<div className="cg-detail__title-row">
				<PageBackButton />
				{icon && <span className="cg-detail__title-icon">{icon}</span>}
				<div className="cg-detail__title-block">
					<h1 className="cg-detail__page-title">{title}</h1>
					{meta && <div className="cg-detail__page-meta">{meta}</div>}
				</div>
			</div>
		</div>
		{actions && <div className="cg-detail__page-actions">{actions}</div>}
	</div>
);

// ── KPI stat band ──────────────────────────────────────────────────
/**
 * Horizontal band of headline stats, as in the detail-page mockups.
 * `tone`: accent (default) | success | danger | warning | neutral
 */
export const KpiStat = ({
	icon,
	label,
	value,
	sub,
	badge,
	tone = 'accent',
	className
}) => (
	<div
		className={`cg-detail__kpi cg-detail__kpi--${tone}${className ? ` ${className}` : ''
			}`}
	>
		{icon && <span className="cg-detail__kpi-icon">{icon}</span>}
		<div className="cg-detail__kpi-body">
			<span className="cg-detail__kpi-label">{label}</span>
			<span className="cg-detail__kpi-value">
				{(() => {
					if (value === null || value === undefined || value === '') {
						return '—';
					}
					const numeric = Number(value);
					return Number.isFinite(numeric) ? Math.round(numeric) : value;
				})()}
			</span>
			{(sub || badge) && (
				<span className="cg-detail__kpi-sub">
					{sub}
					{badge}
				</span>
			)}
		</div>
	</div>
);

export const KpiStatBand = ({ children, className, preferenceKey }) => (
	<KpiPanel preferenceKey={preferenceKey}>
		<div className={`cg-detail__kpi-band${className ? ` ${className}` : ''}`}>
			{children}
		</div>
	</KpiPanel>
);

// ── Tabs ───────────────────────────────────────────────────────────
/**
 * @param tabs      [{ key, label, count? }]
 * @param activeKey currently active tab key
 * @param onChange  (key) => void
 */
export const DetailTabs = ({ tabs, activeKey, onChange }) => (
	<div className="cg-detail__tabs" role="tablist">
		{tabs.map(tab => (
			<button
				key={tab.key}
				type="button"
				role="tab"
				aria-selected={activeKey === tab.key}
				className={`cg-detail__tab${activeKey === tab.key ? ' cg-detail__tab--active' : ''
					}`}
				onClick={() => onChange(tab.key)}
			>
				{tab.label}
				{tab.count !== undefined && tab.count !== null && (
					<span className="cg-detail__tab-count">{tab.count}</span>
				)}
			</button>
		))}
	</div>
);

// ── Panel (card surface with optional header) ──────────────────────
export const DetailPanel = ({
	eyebrow,
	title,
	subtitle,
	actions,
	children,
	className,
	flush = false
}) => (
	<section
		className={`cg-detail__panel${flush ? ' cg-detail__panel--flush' : ''}${className ? ` ${className}` : ''
			}`}
	>
		{(title || eyebrow || actions) && (
			<header className="cg-detail__panel-head">
				<div className="cg-detail__panel-titles">
					{eyebrow && <span className="cg-detail__panel-eyebrow">{eyebrow}</span>}
					{title && <h3 className="cg-detail__panel-title">{title}</h3>}
					{subtitle && (
						<p className="cg-detail__panel-subtitle">{subtitle}</p>
					)}
				</div>
				{actions && <div className="cg-detail__panel-actions">{actions}</div>}
			</header>
		)}
		<div className="cg-detail__panel-body">{children}</div>
	</section>
);

// ── Label / value row ──────────────────────────────────────────────
/**
 * Generic collapsible section for maps, charts and history content shared by
 * all CorrGuard product detail pages.
 */
export const DetailDisclosure = ({
	title,
	subtitle,
	actions,
	children,
	defaultOpen = true,
	className
}) => {
	const [isOpen, setIsOpen] = useState(defaultOpen);
	return (
		<section className={`cg-detail__disclosure${className ? ` ${className}` : ''}`}>
			<header className="cg-detail__disclosure-head">
				<button
					type="button"
					className="cg-detail__disclosure-toggle"
					aria-expanded={isOpen}
					onClick={() => setIsOpen(open => !open)}
				>
					<span className={`cg-detail__disclosure-chevron${isOpen ? ' cg-detail__disclosure-chevron--open' : ''}`}>
						›
					</span>
					<span>
						<strong>{title}</strong>
						{subtitle && <small>{subtitle}</small>}
					</span>
				</button>
				{actions && <div className="cg-detail__disclosure-actions">{actions}</div>}
			</header>
			{isOpen && <div className="cg-detail__disclosure-body">{children}</div>}
		</section>
	);
};

export const DetailRow = ({ label, value, mono }) => (
	<div className="cg-detail__row">
		<span className="cg-detail__row-label">{label}</span>
		<span className={`cg-detail__row-value${mono ? ' cg-font-mono' : ''}`}>
			{value === null || value === undefined || value === '' ? (
				<span className="cg-detail__row-empty">-</span>
			) : (
				value
			)}
		</span>
	</div>
);

// ── States ─────────────────────────────────────────────────────────
export const DetailLoading = ({ label = 'Loading...' }) => (
	<LoadingSpinner text={label} />
);

export const DetailError = ({ message, children }) => (
	<div className="cg-detail__state cg-detail__state--error">
		<p>{message || 'Something went wrong.'}</p>
		{children}
	</div>
);
